import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { FieldArrayType } from '@ngx-formly/core';
import { DateTime } from 'luxon';

import { newTravalerOption } from '@app/utils/constants';

@Component({
  selector: 'traveler-cards',
  templateUrl: './traveler-cards.component.html',
  styleUrls: ['./traveler-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TravelerCardsComponent extends FieldArrayType implements OnInit {
  expanded: boolean[] = [];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private dr: DestroyRef,
  ) {
    super();
  }

  ngOnInit() {
    const count = this.field.fieldGroup?.length ?? 0;
    this.expanded = Array.from({ length: count }, (_, i) => i === 0);

    this.props['expandTraveler'] = (index: number) => this.expandTraveler(index);

    this.formControl?.valueChanges.pipe(takeUntilDestroyed(this.dr)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });

    this.formControl?.statusChanges.pipe(takeUntilDestroyed(this.dr)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  isExpanded(index: number) {
    return !!this.expanded[index];
  }

  toggle(index: number) {
    this.expanded[index] = !this.expanded[index];
    this.changeDetectorRef.markForCheck();
  }

  expandTraveler(index: number) {
    if (index < 0) return;

    this.expanded[index] = true;
    this.changeDetectorRef.markForCheck();
  }

  onHeaderKeydown(event: KeyboardEvent, index: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(index);
    }
  }

  getSummary(index: number): TravelerCardSummaryView {
    const group = this.getPassengerGroup(index);
    if (!group) {
      return { text: 'Add traveler details', isError: false };
    }

    const value = group.getRawValue() as TravelerFormValue;
    const name = getTravelerDisplayName(value);
    const dob = formatBirthday(value.birthday);
    const identifying = name && dob ? `${name}, ${dob}` : name;

    if (isTravelerComplete(group, value)) {
      return { text: identifying || name || '', isError: false };
    }

    if (isTravelerUntouched(value) && !group.touched) {
      return { text: 'Add traveler details', isError: false };
    }

    if (identifying) {
      return { text: identifying, actionText: 'Complete traveler details', isError: false };
    }

    return { text: 'Complete traveler details', isError: true };
  }

  getPassengerGroup(index: number) {
    const array = this.formControl as FormArray | null;
    return array?.at(index) as FormGroup | undefined;
  }
}

type TravelerFormValue = {
  id?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  birthday?: string;
  gender?: string;
  passportCountryId?: string;
};

type TravelerCardSummaryView = {
  text: string;
  actionText?: string;
  isError: boolean;
};

const getTravelerDisplayName = (value: TravelerFormValue) => {
  const parts = [value.firstName, value.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
};

const formatBirthday = (birthday?: string) => {
  if (!birthday) return null;

  const parsed = DateTime.fromISO(birthday);
  if (!parsed.isValid) return null;

  return parsed.toFormat('d MMM yyyy');
};

const isTravelerUntouched = (value: TravelerFormValue) => {
  const hasId = !!value.id && value.id !== newTravalerOption.value;
  const hasNewTraveler = value.id === newTravalerOption.value;
  const hasAnyField = !!(
    value.firstName ||
    value.middleName ||
    value.lastName ||
    value.birthday ||
    value.gender ||
    value.passportCountryId
  );

  return !hasId && !hasNewTraveler && !hasAnyField;
};

const isTravelerComplete = (group: AbstractControl, value: TravelerFormValue) => {
  return (
    group.valid &&
    !!value.firstName &&
    !!value.lastName &&
    !!value.birthday &&
    !!value.gender &&
    !!value.passportCountryId
  );
};
